"use client";

import { useDict } from "@/components/i18n/DictProvider";
import { Clock, Headphones, Mic } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Badge } from "@/components/ui/Badge";
import { DetailHeroBanner } from "@/components/modules/DetailHeroBanner";
import { MetaPill } from "@/components/modules/MetaPill";
import { type Lang } from "@/lib/i18n";
import { detailCrumbs } from "@/lib/routes/detailCrumbs";
import { formatDuration, formatEpisodes } from "@/lib/format";
import { parsePaths } from "./cards/CardShell";
import { moduleHero, moduleHeroAlt } from "@/lib/lecturehub/poster";
import type { ModuleDetail } from "@/lib/lecturehub/types";

export function PodcastHero({
  lang,
  module: m,
  library = false,
  overlayHeader = false,
  cfHash = null,
}: {
  lang: Lang;
  module: ModuleDetail;
  library?: boolean;
  /** Guest/public surface: header overlays the banner → add clearance padding. */
  overlayHeader?: boolean;
  /** Portal.cloudflareAccountHash — without it the hero falls back to the raw
   *  asset `uri` instead of the optimized CF `xl` variant. */
  cfHash?: string | null;
}) {
  const t = useDict();
  const pod = m.podcast;
  const paths = parsePaths(m.keywords);
  const isSeries = pod?.audioType === "simple_series" || pod?.audioType === "section_series";
  const totalDuration = isSeries
    ? (pod?.audios ?? []).reduce((s, a) => s + (a.audio?.duration ?? 0), 0)
    : (pod?.audio?.duration ?? 0);
  const dur = totalDuration > 0 ? formatDuration(totalDuration) : null;

  return (
    <>
      <DetailHeroBanner
        imageUrl={moduleHero(m, cfHash)}
        imageAlt={moduleHeroAlt(m, m.name)}
        overlayHeader={overlayHeader}
      >
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Badge tone="gold">{t.detail.typePodcast}</Badge>
          {isSeries ? <Badge tone="muted">{t.detail.typeSeries}</Badge> : null}
        </div>
        <h1 className="max-w-4xl !text-[28px] !font-semibold !leading-[1.08] !tracking-tight !text-white md:!text-[44px]">
          {m.name}
        </h1>
        {/* Abstract intentionally NOT rendered here — it now lives as the
            editorial deck inside the body PodcastView. */}
        <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-white/85">
          {dur ? <MetaPill Icon={Clock}>{dur}</MetaPill> : null}
          {pod?.audios?.length ? (
            <MetaPill Icon={Mic}>{formatEpisodes(pod.audios.length, lang, t.units)}</MetaPill>
          ) : pod?.audio ? (
            <MetaPill Icon={Headphones}>{t.detail.singleEpisode}</MetaPill>
          ) : null}
        </div>
        {paths.length > 0 ? (
          <div className="mt-5 flex flex-wrap gap-1.5">
            {paths.slice(0, 6).map((p) => (
              <span
                key={p}
                className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white/75"
              >
                {p}
              </span>
            ))}
          </div>
        ) : null}
      </DetailHeroBanner>
      <section>
        <Container className="pt-5">
          <Breadcrumb items={detailCrumbs(t, lang, "podcasts", m.name, library)} />
        </Container>
      </section>
    </>
  );
}
